import { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { MessageCircle, X, Send, Sparkles, Loader2, User, Bot } from 'lucide-react';

const AppChatbot = ({ appName, subtitle, getResponse, suggestedPrompts, tooltipText, tooltipDuration = 7000 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: `Hi, I'm the ${appName} AI assistant. ${subtitle}` },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(!!tooltipText);
  const messagesEndRef = useRef(null);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Re-seed greeting + dismiss tooltip whenever subtitle (context) changes
  useEffect(() => {
    setMessages([{ role: 'assistant', content: `Hi, I'm the ${appName} AI assistant. ${subtitle}` }]);
    setTooltipVisible(!!tooltipText);
  }, [subtitle, appName, tooltipText]);

  // Auto-dismiss tooltip
  useEffect(() => {
    if (!tooltipVisible) return;
    const t = setTimeout(() => setTooltipVisible(false), tooltipDuration);
    return () => clearTimeout(t);
  }, [tooltipVisible, tooltipDuration]);

  const handleSend = (text) => {
    const msg = text || input;
    if (!msg.trim()) return;
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setInput('');
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => [...prev, { role: 'assistant', content: getResponse(msg) }]);
      setIsTyping(false);
    }, 700 + Math.random() * 500);
  };

  return (
    <>
      {!isOpen && (
        <>
          {/* Tooltip bubble (contextual hint) */}
          {tooltipVisible && tooltipText && (
            <div
              className="fixed bottom-[140px] right-6 z-[9999] rounded-xl bg-white border border-slate-200 shadow-md px-3 py-2 text-[11px] text-slate-700 max-w-[240px] animate-in fade-in slide-in-from-bottom-2"
              data-testid="chatbot-tooltip"
            >
              <div className="flex items-start gap-2">
                <Sparkles className="h-3.5 w-3.5 text-slate-900 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">{tooltipText}</p>
                </div>
                <button
                  onClick={() => setTooltipVisible(false)}
                  className="text-slate-300 hover:text-slate-500"
                  style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                  aria-label="Dismiss tip"
                  data-testid="chatbot-tooltip-dismiss"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
              {/* Bottom-pointing chevron anchored to the icon */}
              <div className="absolute -bottom-1.5 right-5 h-3 w-3 rotate-45 bg-white border-r border-b border-slate-200" />
            </div>
          )}
          <button onClick={() => { setIsOpen(true); setTooltipVisible(false); }} className="fixed bottom-[70px] right-6 z-[9999] h-14 w-14 rounded-full bg-black text-white shadow-lg hover:bg-gray-800 flex items-center justify-center transition-all hover:scale-105" data-testid="chatbot-toggle">
            <MessageCircle className="h-6 w-6" />
            {tooltipVisible && tooltipText && (
              <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-500 border-2 border-white animate-pulse" aria-hidden />
            )}
          </button>
        </>
      )}
      {isOpen && (
        <div className="fixed bottom-[70px] right-6 z-[9999] w-[400px] h-[560px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="chatbot-window">
          <div className="bg-black px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg bg-white/10 flex items-center justify-center"><Sparkles className="h-4 w-4 text-white" /></div>
              <div><p className="text-sm font-semibold text-white">{appName} AI</p><p className="text-[10px] text-slate-400">Assistant</p></div>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/10" onClick={() => setIsOpen(false)} data-testid="chatbot-close"><X className="h-4 w-4" /></Button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={cn("flex gap-2", msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                {msg.role === 'assistant' && <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0 mt-0.5"><Bot className="h-3.5 w-3.5 text-slate-600" /></div>}
                <div className={cn("max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed", msg.role === 'user' ? "bg-black text-white rounded-br-md" : "bg-slate-100 text-slate-800 rounded-bl-md")} data-testid={`chat-message-${i}`}>
                  {msg.content.split('\n').map((line, j) => (
                    <p key={j} className={j > 0 ? 'mt-1' : ''}>
                      {line.split(/(\*\*.*?\*\*)/).map((part, k) => part.startsWith('**') && part.endsWith('**') ? <strong key={k}>{part.slice(2, -2)}</strong> : part)}
                    </p>
                  ))}
                </div>
                {msg.role === 'user' && <div className="h-7 w-7 rounded-full bg-slate-800 flex items-center justify-center shrink-0 mt-0.5"><User className="h-3.5 w-3.5 text-white" /></div>}
              </div>
            ))}
            {isTyping && (
              <div className="flex gap-2">
                <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0"><Bot className="h-3.5 w-3.5 text-slate-600" /></div>
                <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 text-slate-500 animate-spin" /><span className="text-xs text-slate-500">Thinking...</span></div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          {messages.length <= 2 && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5">
              {suggestedPrompts.map((prompt, i) => (
                <button key={i} onClick={() => handleSend(prompt)} className="text-[11px] px-2.5 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors" data-testid={`chat-suggestion-${i}`}>{prompt}</button>
              ))}
            </div>
          )}
          <div className="border-t p-3 flex gap-2 shrink-0">
            <Input placeholder="Ask a question..." value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend()} className="flex-1 text-sm" data-testid="chatbot-input" />
            <Button size="icon" className="bg-black hover:bg-gray-800 h-9 w-9 shrink-0" onClick={() => handleSend()} disabled={!input.trim()} data-testid="chatbot-send"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </>
  );
};

export default AppChatbot;
